import {
  BACKEND_API,
  DeliveryStatus,
  DeliveryStatusWithUpdateTime,
  Order,
} from '@trwl/backend.api';
import { DELIVERY_DURATION } from '@trwl/constants';
import { createEvent, createStore } from 'effector';
import { ReactElement } from 'react';
import { AiOutlineDeliveredProcedure } from 'react-icons/ai';
import { BsFillBoxSeamFill, BsFillSendCheckFill } from 'react-icons/bs';
import { FaCar, FaCheck, FaRubleSign, FaUserCheck } from 'react-icons/fa';
import { toast } from 'sonner';

export interface OrderSection {
  icon: string | ReactElement;
  statusKey: string;
  color: string;
  title: string;
  updatedAt?: Date;
  active: boolean;
  hidden: boolean;
  tilda: boolean;
}

export const statusSectionIcons: {
  [key: string]: string | ReactElement;
} = {
  CREATED: (
    <FaCheck
      style={{
        color: 'white',
      }}
    />
  ),
  IN_TRANSIT_TO_WAREHOUSE: (
    <FaCar
      style={{
        color: 'white',
      }}
    />
  ),
  SENT_TO_MOSCOW: (
    <BsFillSendCheckFill
      style={{
        color: 'white',
      }}
    />
  ),
  ARRIVED_AT_WAREHOUSE: (
    <BsFillBoxSeamFill
      style={{
        color: 'white',
      }}
    />
  ),
  ARRIVED_AT_FOREIGN_WAREHOUSE: (
    <BsFillBoxSeamFill
      style={{
        color: 'white',
      }}
    />
  ),
  PURCHASED: (
    <FaRubleSign
      style={{
        color: 'white',
      }}
    />
  ),
  DELIVERED_TO_CDEK: (
    <AiOutlineDeliveredProcedure
      style={{
        color: 'white',
      }}
    />
  ),
  DELIVERED_TO_CLIENT: (
    <FaUserCheck
      style={{
        color: 'white',
      }}
    />
  ),
};

function generateGradient(
  startColor: string,
  endColor: string,
  steps: number,
): string[] {
  if (steps === 1) {
    return [startColor];
  }
  const start = parseInt(startColor.slice(1), 16);
  const end = parseInt(endColor.slice(1), 16);

  const r1 = (start >> 16) & 0xff;
  const g1 = (start >> 8) & 0xff;
  const b1 = start & 0xff;

  const r2 = (end >> 16) & 0xff;
  const g2 = (end >> 8) & 0xff;
  const b2 = end & 0xff;

  const colors = [];

  for (let i = 0; i < steps; i++) {
    const r = Math.round(r1 + ((r2 - r1) * i) / (steps - 1));
    const g = Math.round(g1 + ((g2 - g1) * i) / (steps - 1));
    const b = Math.round(b1 + ((b2 - b1) * i) / (steps - 1));
    colors.push(
      `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`,
    );
  }

  return colors;
}

export const $deliveryStatuses = createStore<DeliveryStatus[]>([]);
export const setDeliveryStatuses = createEvent<DeliveryStatus[]>();

export const $sections = createStore<OrderSection[]>([]);
const setSections = createEvent<OrderSection[]>();

export const $currentOrder = createStore<Order | null>(null);

const setCurrentOrder = createEvent<Order | null>();

const getStatuses = (
  statuses: DeliveryStatusWithUpdateTime[],
  order: Order,
): OrderSection[] => {
  const deliveryStatuses = $deliveryStatuses
    .getState()
    .sort((a, b) => a.sort - b.sort);

  const filteredStatuses = statuses.filter((status) =>
    Object.hasOwn(statusSectionIcons, status.statusKey),
  );

  const colors = generateGradient(
    '#19b248',
    '#19b2a3',
    deliveryStatuses.length,
  );

  const result: OrderSection[] = deliveryStatuses.map((status, index) => {
    const deliveryStatusWithUpdateTime = filteredStatuses.find(
      (item) => item.statusKey === status.statusKey,
    );
    const active = deliveryStatusWithUpdateTime !== undefined;

    return {
      statusKey: status.statusKey,
      icon: statusSectionIcons[status.statusKey],
      title: status.title,
      updatedAt: deliveryStatusWithUpdateTime
        ? new Date(deliveryStatusWithUpdateTime.updatedAt)
        : undefined,
      color: active ? colors[index] : '#9b9b9b',
      active,
      hidden: false,
      tilda: false,
    };
  });

  const arrivedAtWirehouse = result.find(
    (status) => status.statusKey === 'ARRIVED_AT_WAREHOUSE',
  );

  const sentToMoscow = result.find(
    (status) => status.statusKey === 'SENT_TO_MOSCOW',
  );

  if (
    arrivedAtWirehouse &&
    sentToMoscow?.active &&
    !arrivedAtWirehouse.active
  ) {
    const createdAt = new Date(sentToMoscow.updatedAt ?? Date.now().valueOf());
    const defaultDeliveryDuration = order.airplaneDelivery
      ? DELIVERY_DURATION.AIRPLANE.country_delivery
      : DELIVERY_DURATION.DEFAULT.country_delivery;
    const deliveryDuration =
      (order.deliveryDays ?? 0) + defaultDeliveryDuration;
    createdAt.setDate(createdAt.getDate() + deliveryDuration);
    arrivedAtWirehouse.updatedAt = createdAt;
    arrivedAtWirehouse.tilda = true;
  }
  return result;
};
export const getOrder = (orderId: string) => {
  const promise = async () => {
    await BACKEND_API.DeliveryApi.getDeliveryStatuses().then(({ data }) => {
      setDeliveryStatuses(data);
    });
    const order = await BACKEND_API.OrderApi.getMyOrderById(orderId)
      .then(({ data }) => {
        return data;
      })
      .catch(() => {
        return null;
      });
    setCurrentOrder(order);
    if (order) {
      await BACKEND_API.DeliveryApi.getDeliveryStatusesByOrderId(orderId).then(
        ({ data }) => {
          setSections(getStatuses(data, order));
        },
      );
    }
  };

  toast.promise(promise, {
    loading: `Загрузка...`,
    success: () => {
      return `Заказ #${orderId} был найден!`;
    },
    error: 'Заказ не найден!',
  });
};

$sections.on(setSections, (_, payload) => [...payload]);
$deliveryStatuses.on(setDeliveryStatuses, (_, payload) => payload);
$currentOrder.on(setCurrentOrder, (_, payload) => payload);
